import { useEffect, useState } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { Loader2, FileIcon, ZoomIn, ZoomOut } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { pdfjsLib } from '@/lib/pdfWorker';

interface DocumentPreviewProps {
  filePath: string;
  fileName: string;
}

export function DocumentPreview({ filePath, fileName }: DocumentPreviewProps) {
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [zoom, setZoom] = useState(100);
  const [pdfPages, setPdfPages] = useState<string[]>([]);

  const fileExtension = fileName.split('.').pop()?.toLowerCase();
  const isImage = ['jpg', 'jpeg', 'png', 'gif', 'webp'].includes(fileExtension || '');
  const isPdf = fileExtension === 'pdf';

  useEffect(() => {
    async function loadPreview() {
      try {
        setIsLoading(true);
        setError(null);

        // Get signed URL
        const { data, error: urlError } = await supabase.storage
          .from('partner-documents')
          .createSignedUrl(filePath, 3600);

        if (urlError) {
          // Check for specific 404 error (file not found in storage)
          if (urlError.message?.includes('not found') || urlError.message?.includes('Object not found')) {
            setError('file_not_found');
            setIsLoading(false);
            return;
          }
          throw urlError;
        }
        if (!data?.signedUrl) throw new Error('Failed to get preview URL');

        setPreviewUrl(data.signedUrl);

        // If PDF, render pages as images
        if (isPdf) {
          // Fetch PDF as ArrayBuffer to avoid CORS issues
          const pdfResponse = await fetch(data.signedUrl);
          if (!pdfResponse.ok) {
            throw new Error(`Failed to fetch PDF: ${pdfResponse.statusText}`);
          }
          const pdfArrayBuffer = await pdfResponse.arrayBuffer();
          
          const loadingTask = pdfjsLib.getDocument({ data: pdfArrayBuffer });
          const pdf = await loadingTask.promise;
          const pageImages: string[] = [];

          // Render first 3 pages for preview
          const numPages = Math.min(pdf.numPages, 3);
          for (let i = 1; i <= numPages; i++) {
            const page = await pdf.getPage(i);
            const viewport = page.getViewport({ scale: 1.5 });
            const canvas = document.createElement('canvas');
            const context = canvas.getContext('2d');
            
            if (context) {
              canvas.height = viewport.height;
              canvas.width = viewport.width;

              await page.render({
                canvasContext: context,
                viewport: viewport,
              }).promise;

              pageImages.push(canvas.toDataURL());
            }
          }

          setPdfPages(pageImages);
        }
      } catch (err) {
        console.error('Error loading preview:', err);
        setError('Failed to load document preview');
      } finally {
        setIsLoading(false);
      }
    }

    loadPreview();
  }, [filePath, fileName, isPdf]);

  if (isLoading) {
    return (
      <div className="flex h-full items-center justify-center bg-muted/50 overflow-hidden">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (error === 'file_not_found') {
    return (
      <div className="flex h-full flex-col items-center justify-center gap-4 bg-muted/50 p-8 text-center overflow-hidden">
        <FileIcon className="h-12 w-12 text-destructive" />
        <div className="space-y-2">
          <p className="font-semibold text-foreground">File Not Found in Storage</p>
          <p className="text-sm text-muted-foreground">
            The file record exists in the database but the actual file is missing from storage.
          </p>
          <p className="text-xs text-muted-foreground">
            This may have occurred due to an incomplete upload or manual deletion.
          </p>
        </div>
        <div className="flex gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => window.location.reload()}
          >
            Refresh
          </Button>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex h-full flex-col items-center justify-center gap-4 bg-muted/50 p-8 text-center overflow-hidden">
        <FileIcon className="h-12 w-12 text-destructive" />
        <div className="space-y-2">
          <p className="font-semibold text-foreground">Failed to Load Preview</p>
          <p className="text-sm text-muted-foreground">{error}</p>
        </div>
      </div>
    );
  }

  if (!previewUrl) {
    return (
      <div className="flex h-full flex-col items-center justify-center gap-2 bg-muted/50 p-8 text-center">
        <FileIcon className="h-12 w-12 text-muted-foreground" />
        <p className="text-sm text-muted-foreground">Preview not available</p>
      </div>
    );
  }

  return (
    <div className="flex h-full flex-col bg-muted/50 overflow-hidden">
      {/* Zoom Controls */}
      <div className="flex items-center justify-between border-b bg-background p-2 flex-shrink-0">
        <span className="text-sm text-muted-foreground truncate">{fileName}</span>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => setZoom(Math.max(50, zoom - 25))}
            disabled={zoom <= 50}
          >
            <ZoomOut className="h-4 w-4" />
          </Button>
          <span className="text-sm font-medium">{zoom}%</span>
          <Button
            variant="outline"
            size="sm"
            onClick={() => setZoom(Math.min(200, zoom + 25))}
            disabled={zoom >= 200}
          >
            <ZoomIn className="h-4 w-4" />
          </Button>
        </div>
      </div>

      {/* Document Content */}
      <div className="flex-1 overflow-auto p-4 min-h-0">
        {isImage && (
          <div className="flex justify-center">
            <img
              src={previewUrl}
              alt={fileName}
              style={{ width: `${zoom}%` }}
              className="max-w-full rounded-lg shadow-lg"
            />
          </div>
        )}

        {isPdf && pdfPages.length > 0 && (
          <div className="flex flex-col gap-4">
            {pdfPages.map((pageImage, idx) => (
              <div key={idx} className="flex justify-center">
                <div className="relative">
                  <img
                    src={pageImage}
                    alt={`Page ${idx + 1}`}
                    style={{ width: `${zoom}%` }}
                    className="max-w-full rounded-lg shadow-lg"
                  />
                  <div className="absolute bottom-2 right-2 rounded bg-background/90 px-2 py-1 text-xs font-medium">
                    Page {idx + 1}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {!isImage && !isPdf && (
          <div className="flex h-full flex-col items-center justify-center gap-2 text-center">
            <FileIcon className="h-16 w-16 text-muted-foreground" />
            <p className="text-sm text-muted-foreground">
              Preview not available for this file type
            </p>
            <Button
              variant="outline"
              size="sm"
              onClick={() => window.open(previewUrl, '_blank')}
            >
              Open in New Tab
            </Button>
          </div>
        )}
      </div>
    </div>
  );
}
